import type { PreviewReview } from '@flexbyt/shared';

/**
 * Photographs for the sample reviewers, where one has been made.
 *
 * The illustrated avatars in `demo-reviews.ts` are right inside a widget, where
 * they are one small circle among many and a drawing reads as "example". On an
 * integration page's "choose the ones you want shown" list they are the largest
 * thing on each card, and a column of cartoons makes that list look like a
 * mock-up rather than the reviews somebody is about to pick from.
 *
 * These are AI-generated portraits of people who do not exist — every one of
 * these reviewers is fictional, and the list they appear in says it is showing
 * sample reviews. Keyed by name rather than stored on the review, so the
 * sample sets (`sample-sets.ts`) can put them on everything built from them
 * while the homepage's own showcases keep their drawings; and listed, so a
 * name without a file falls back to the drawing instead of a broken image.
 */
const PHOTOGRAPHED = new Set<string>([
  // One per file in `public/images/sample/people`.
  'ade-balogun',
  'aisha-rahman',
  'alice-kavanagh',
  'andre-costa',
  'anya-sokolova',
  'aoife-byrne',
  'bea-whitmore',
  'ben-okafor',
  'bridget-kelly',
  'callum-hayes',
  'camille-roux',
  'chen-wei',
  'clara-nguyen',
  'daniel-brennan',
  'derek-simmons',
  'diego-herrera',
  'elena-petrova',
  'ellis-warren',
  'fatima-zahra',
  'freya-lindqvist',
  'george-mbeki',
  'grace-mensah',
  'hana-suzuki',
  'hannah-meyer',
  'helen-prescott',
  'hugo-lambert',
  'ian-sutcliffe',
  'ines-ferreira',
  'ingrid-m-ller',
  'jonah-pike',
  'jonas-weber',
  'julia-sandberg',
  'karl-jensen',
  'kwame-asante',
  'lena-fischer',
  'liam-o-sullivan',
  'lucia-marin',
  'marcus-bell',
  'marek-kowalski',
  'margaret-cole',
  'marta-nowicka',
  'marta-silva',
  'mateo-rivas',
  'meera-joshi',
  'nadia-karim',
  'neil-armitage',
  'nina-halvorsen',
  'oliver-grant',
  'owen-tanaka',
  'paulo-almeida',
  'peter-nowak',
  'priya-raman',
  'rachel-whitfield',
  'ravi-patel',
  'rowan-hale',
  'sasha-ivanov',
  'saskia-vermeer',
  'sinead-doyle',
  'sofia-marchetti',
  'stefan-bauer',
  'tom-baptiste',
  'tomas-ferreira',
  'tunde-adeyemi',
  'yusuf-demir',
]);

function slug(name: string): string {
  return name
    .replace(/’/g, "'")
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

/** The sample reviewer's photograph, or null — for a real review, always null. */
export function samplePhoto(review: PreviewReview): string | null {
  if (!review.isSample) return null;
  const key = slug(review.authorName);
  return PHOTOGRAPHED.has(key) ? `/images/sample/people/${key}.jpg` : null;
}
